import React, {useState, useMemo, useCallback, useEffect} from 'react';
import _ from 'lodash';
import {map} from 'nanostores';
import {useStore} from '@nanostores/react';
import {Chart, BarController, LineController, CategoryScale, LinearScale,
    BarElement, PointElement, LineElement,
    Tooltip as ChartTooltip} from 'chart.js';
import {Autocomplete, Box, Card, Container, Divider, IconButton, LinearProgress,
    MenuItem, Select, Skeleton, Stack, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography,
    useTheme} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import withMainContainer from '../components/WithMainContainer.js';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import api from '../util/api.js';
import SCTextField from '../components/SCTextField.js';
import SCCheckbox from '../components/SCCheckbox.js';
import {formatAge, formatTime} from '../util/util.js';
import {DAY, MIN, MONTH, qw} from '../util.js';
import SCButton from '../components/SCButton.js';
import {openModal} from '../components/Modals.js';
import SCJsonView from '../components/SCJsonView.js';
import SCDatePicker from '../components/SCDatePicker.js';
import SkeletonWrap from '../components/SkeletonWrap.js';
import Pagination from '../components/Pagination.js';
import SCTimePicker from '../components/SCTimePicker.js';
import SCNumberField from '../components/SCNumberField.js';
import {useDebounce} from '../util/react_hooks.js';

Chart.register(
    BarController,
    LineController,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ChartTooltip,
);

const $errorsFilter = map({
    search: '',
    allEnvs: false,
    from: new Date(Date.now()-MONTH),
    to: new Date(Date.now()+DAY),
});

const $jobsFilter = map({
    search: '',
    failedOnly: false,
    allEnvs: false,
    type: 'all',
    from: new Date(Date.now()-MONTH),
    to: new Date(Date.now()+DAY),
});

const $statsFilter = map({
    keys: [],
    from: new Date(Date.now()-DAY),
    to: new Date(),
    sections: 80,
    lerp: false,
});

const useJobs = (opt={})=>{
    let {data, isLoading} = useQuery({
        queryKey: ['jobs', opt],
        queryFn: ()=>api.admin.jobs(opt),
    });
    return [data, isLoading];
};

const useJobTypes = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['job_types'],
        queryFn: ()=>api.admin.jobTypes(),
    });
    return [data?.types, isLoading];
};

const useErrors = (opt={})=>{
    let {data, isLoading} = useQuery({
        queryKey: ['errors', opt],
        queryFn: ()=>api.admin.errors(opt),
    });
    return [data, isLoading];
};

const JobTypeDropdown = props=>{
    let {value, onChange: _onChange} = props;
    let [jobTypes, loading] = useJobTypes();
    let onChange = useCallback(ev=>_onChange?.(ev.target.value, ev), [_onChange]);
    return <SkeletonWrap skeleton={loading}>
      <Select value={value} onChange={onChange}>
        <MenuItem value="all">כל הסוגים</MenuItem>
        {jobTypes?.map(type=><MenuItem key={type} value={type}>{type}</MenuItem>)}
      </Select>
    </SkeletonWrap>;
};

const JobsFilterComp = props=>{
    let {store} = props;
    let value = useStore(store);
    return <Stack direction="column" gap={2}>
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <Box maxWidth={400}>
          <SCTextField label="חיפוש" value={value.search}
            onChange={v=>store.setKey('search', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCCheckbox label="רק ג'ובים שכשלו" value={value.failedOnly}
            onChange={v=>store.setKey('failedOnly', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCCheckbox label="כל הסביבות" value={value.allEnvs}
            onChange={v=>store.setKey('allEnvs', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCDatePicker value={value.from} onChange={v=>store.setKey('from', v)}
            label="מתאריך"/>
        </Box>
        <Box maxWidth={400}>
          <SCDatePicker value={value.to} onChange={v=>store.setKey('to', v)}
            label="עד תאריך"/>
        </Box>
        <Stack direction="column" maxWidth={400}>
          <Typography>סוג</Typography>
          <JobTypeDropdown value={value.type} onChange={v=>store.setKey('type', v)}/>
        </Stack>
      </Stack>
    </Stack>;
};

const ErrorsFilterComp = props=>{
    let {store} = props;
    let value = useStore(store);
    return <Stack direction="column" gap={2}>
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <Box maxWidth={400}>
          <SCTextField label="חיפוש" value={value.search}
            onChange={v=>store.setKey('search', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCCheckbox label="כל הסביבות" value={value.allEnvs}
            onChange={v=>store.setKey('allEnvs', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCDatePicker value={value.from} onChange={v=>store.setKey('from', v)}
            label="מתאריך"/>
        </Box>
        <Box maxWidth={400}>
          <SCDatePicker value={value.to} onChange={v=>store.setKey('to', v)}
            label="עד תאריך"/>
        </Box>
      </Stack>
    </Stack>;
};

const StatsKeysAutocomplete = ()=>{
    let store = useStore($statsFilter);
    let [inputValue, setInputValue] = useState('');
    let [allKeys, loading] = useStatsKeys();
    let selectableKeys = useMemo(()=>_.difference(allKeys, store.keys),
        [allKeys, store.keys]);
    if (loading)
        return <LinearProgress/>;
    return <Stack flexGrow={1}>
      <Autocomplete
        multiple
        value={store.keys}
        onChange={(__, v)=>$statsFilter.setKey('keys', v)}
        inputValue={inputValue}
        onInputChange={(__, v)=>setInputValue(v)}
        options={selectableKeys}
        renderInput={p=><SCTextField label="סוג" {...p}/>}
      />
    </Stack>;
};

const StatsRefreshButton = ()=>{
    let queryClient = useQueryClient();
    let refresh = useCallback(()=>queryClient
        .invalidateQueries({queryKey: ['stats']}), [queryClient]);
    return <IconButton size="small" onClick={refresh} color="primary">
      <RefreshIcon/>
    </IconButton>
};

const StatsGranularityInput = ()=>{
    let filter = useStore($statsFilter);
    return <Stack width="80px">
      <SCNumberField
        size="small"
        hint="מקטעים"
        value={filter.sections}
        onChange={v=>$statsFilter.setKey('sections', v)}
        min={8}
        max={100}/>
    </Stack>;
};

const StatsLerpCheckbox = ()=>{
    let filter = useStore($statsFilter);
    return <Stack maxWidth="200px">
      <SCCheckbox
        value={filter.lerp}
        onChange={v=>$statsFilter.setKey('lerp', v)}
        label="lerp"/>
    </Stack>;
};

const StatsFilterComp = ()=>{
    let filter = useStore($statsFilter);
    let [live, setLive] = useState();
    useEffect(()=>{
        if (!live)
            return;
        $statsFilter.setKey('to', new Date());
        let itvl = setInterval(()=>$statsFilter.setKey('to', new Date()), MIN);
        return ()=>clearInterval(itvl);
    }, [live]);
    return <Stack direction="column" gap={2}>
      <Stack direction="row" gap={2} alignItems="end" flexWrap="wrap">
        <Stack maxWidth="200px">
          <SCDatePicker value={filter.from} onChange={v=>$statsFilter.setKey('from', v)}
            label="מתאריך"/>
        </Stack>
        <SCTimePicker value={filter.from} onChange={v=>$statsFilter.setKey('from', v)}
          label="משעה"/>
      </Stack>
      <Stack direction="row" gap={2} alignItems="end" flexWrap="wrap">
        <Stack maxWidth="200px">
          <SCDatePicker value={filter.to} onChange={v=>$statsFilter.setKey('to', v)}
            label="עד תאריך"/>
        </Stack>
        <SCTimePicker value={filter.to} onChange={v=>$statsFilter.setKey('to', v)}
          label="עד שעה"/>
        <SCButton text="עכשיו" variant="outlined"
          onClick={()=>$statsFilter.setKey('to', new Date())}/>
        <Stack width="100px">
          <SCCheckbox label="לייב" value={live} onChange={setLive}/>
        </Stack>
      </Stack>
      <StatsKeysAutocomplete/>
      <Stack direction="row" gap={2} alignItems="start" flexWrap="wrap">
        <StatsRefreshButton/>
        <StatsGranularityInput/>
        <StatsLerpCheckbox/>
      </Stack>
    </Stack>;
};

const TableHeader = props=>{
    let {children} = props;
    return <TableCell align="right">
      <Typography fontWeight="bold" marginTop={1} marginBottom={1}>
        {children}
      </Typography>
    </TableCell>;
};

const JobsTab = ()=>{
    let theme = useTheme();
    let filter = useStore($jobsFilter);
    let [page, setPage] = useState(0);
    let [pageSize, setPageSize] = useState(10);
    let opt = useMemo(()=>({...filter, page, pageSize}),
        [filter, page, pageSize]);
    let [jobs, jobsLoading] = useJobs(opt);
    return <Stack direction="column" gap={2}>
      <JobsFilterComp store={$jobsFilter}/>
      {jobsLoading&&<LinearProgress/>}
      <TableContainer component={Card}>
        <Table sx={{minWidth: 650}}>
          <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
            <TableRow>
              <TableHeader>ID</TableHeader>
              <TableHeader>סוג</TableHeader>
              <TableHeader>נוצר ב</TableHeader>
              <TableHeader>ריצה אחרונה</TableHeader>
              <TableHeader>ריצה הבאה</TableHeader>
              <TableHeader>כשלונות</TableHeader>
              <TableHeader>תוכן</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs?.data?.map(j=><TableRow key={j.data.id}>
              <TableCell align="right">
                <Typography>
                  {j.data.id}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {j.name}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {!!j.data.created&&formatTime(j.data.created)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {!!j.lastRunAt&&formatTime(j.lastRunAt)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {!!j.nextRunAt&&formatTime(j.nextRunAt)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title={j.failReason}>
                  <Typography color={j.failCount ? theme.palette.error.main : theme.palette.success.main}>
                    {j.failCount||0}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <SCButton size="small" text="צפייה בתוכן" variant="outlined" onClick={()=>openModal({
                    header: 'תוכן',
                    size: 'lg',
                    content: <SCJsonView value={j.data}/>,
                })}/>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination total={jobs?.total||0} page={page} pageSize={pageSize}
        setPage={setPage} setPageSize={setPageSize}/>
    </Stack>;
};

const ErrorsTab = ()=>{
    let filter = useStore($errorsFilter);
    let [page, setPage] = useState(0);
    let [pageSize, setPageSize] = useState(10);
    let opt = useMemo(()=>({...filter, page, pageSize}),
        [filter, page, pageSize]);
    let [errors, errorloading] = useErrors(opt);
    return <Stack direction="column" gap={2}>
      <ErrorsFilterComp store={$errorsFilter}/>
      {errorloading&&<LinearProgress/>}
      <TableContainer component={Card}>
        <Table sx={{minWidth: 650}}>
          <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
            <TableRow>
              <TableHeader>הודעה</TableHeader>
              <TableHeader>נוצר ב</TableHeader>
              <TableHeader>סביבה</TableHeader>
              <TableHeader>תוכן</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors?.data?.map(j=><TableRow key={j.id}>
              <TableCell align="right">
                <Typography>
                  {j.message}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {!!j.ts&&formatTime(j.ts)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {j.env}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <SCButton size="small" text="צפייה בתוכן" variant="outlined" onClick={()=>openModal({
                    header: 'תוכן',
                    size: 'lg',
                    content: <SCJsonView value={j.payload}/>,
                })}/>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination total={errors?.total||0} page={page} pageSize={pageSize}
        setPage={setPage} setPageSize={setPageSize}/>
    </Stack>;
};

const LINE_CHART_DIVISIONS = 24;

const LINE_CHART_COLORS = [
    'rgb(75, 192, 192)',
    'rgb(224, 76, 152)',
    'rgb(114, 234, 83)',
    'rgb(32, 93, 147)',
    'rgb(235, 207, 68)',
    'rgb(88, 32, 147)',
    'rgb(229, 151, 61)',
    'rgb(152, 79, 125)',
    'rgb(24, 142, 132)',
    'rgb(60, 140, 34)',
    'rgb(182, 129, 117)',
];

const lerp = (start, end, amt) => (1-amt)*start+amt*end;

const useStatsKeys = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['stats', 'stats_keys'],
        queryFn: ()=>api.admin.getStatsKeys(),
        refetchInterval: MIN,
    });
    return [data?.keys, isLoading];
};

const useStatsLatest = key=>{
    let {data, isLoading} = useQuery({
        queryKey: ['stats', 'stats_latest', key],
        queryFn: ()=>api.admin.getStatsLatest(key),
        enabled: !!key,
        refetchInterval: MIN,
    });
    return [data?.latest, isLoading];
};

const useStatsPoints = (keys, opt={})=>{
    let from = useDebounce(opt.from, 1000);
    let to = useDebounce(opt.to, 1000);
    let {data, isLoading} = useQuery({
        queryKey: ['stats', 'stats_points', keys, {from, to}],
        queryFn: ()=>api.admin.getStatsPoints(keys, {from, to}),
        enabled: !!keys,
        placeholderData: _.identity,
        refetchInterval: MIN,
    });
    return [data?.points, isLoading];
};

const StatsGraph = ()=>{
    let filter = useStore($statsFilter);
    let [points, loading] = useStatsPoints(filter.keys,
        _.pick(filter, qw`from to`));
    let [chartEl, setChartEl] = useState();
    useEffect(()=>{
        if (!chartEl||!points)
            return;
        let timestamps = [];
        for (let i = 0; i<filter.sections; i++)
            timestamps.push(+filter.from+(i/filter.sections)*(+filter.to-filter.from));
        let datasets = [];
        let entries = _.entries(points);
        for (let idx in entries)
        {
            let [k, v] = entries[idx];
            let dataset = {
                label: k,
                fill: false,
                borderColor: LINE_CHART_COLORS[idx],
                data: [],
            };
            let lastPointIdx;
            for (let i = 0; i<timestamps.length; i++)
            {
                let ts = timestamps[i];
                let next = timestamps[i+1];
                let validPoints = v.filter(p=>{
                    let pointTime = +new Date(p.ts);
                    if (next)
                        return pointTime>=ts&&pointTime<next;
                    return pointTime>=ts;
                });
                let pointValue = validPoints.pop()?.value;
                dataset.data.push(pointValue);
                // fill gaps
                if (pointValue!==undefined)
                {
                    if (lastPointIdx!==undefined&&lastPointIdx!=i-1)
                    {
                        let lastPointValue = dataset.data[lastPointIdx];
                        for (let j = lastPointIdx+1; j<i; j++)
                        {
                            if (filter.lerp)
                            {
                                dataset.data[j] = lerp(lastPointValue, pointValue,
                                    (j-lastPointIdx)/(i-lastPointIdx));
                            }
                            else
                            {
                                dataset.data[j] = lastPointValue;
                            }
                        }
                    }
                    lastPointIdx = i;
                }
            }
            datasets.push(dataset);
        }
        if (!datasets.length)
            return;
        let c = new Chart(chartEl, {
            type: 'line',
            data: {
                labels: timestamps.map(formatTime),
                datasets,
            },
            options: {animation: {duration: 0}},
        });
        return ()=>c.destroy();
    }, [chartEl, points, filter]);
    if (loading)
        return <Typography>טוען...</Typography>;
    if (!_.keys(points).length)
        return <Typography>לא נמצאו נתונים</Typography>;
    return <canvas ref={setChartEl}></canvas>;
};

const StatKeyContainer = props=>{
    let {value} = props;
    let [latest, loading] = useStatsLatest(value);
    let filter = useStore($statsFilter);
    return <Stack
      direction="row"
      gap={3}
      justifyContent="space-between"
      alignItems="center"
      padding={2}
      sx={{border: '1px solid #CCC', borderRadius: '4px'}}
      flexWrap="wrap"
    >
      <Typography>
        {value}
      </Typography>
      <Stack direction="row" gap={2} alignItems="center">
        {loading ? <Skeleton width={100} height={40} variant="rounded"/> : <Typography color="primary" fontWeight="bold">
          {latest?.value}
        </Typography>}
        {loading ? <Skeleton width={100} height={40} variant="rounded"/> : <Typography fontStyle="italic">
          {!!latest?.ts&&formatAge(latest.ts)}
        </Typography>}
        <IconButton size="small"
          onClick={()=>$statsFilter.setKey('keys', filter.keys.filter(k=>k!=value))}>
          <CloseIcon/>
        </IconButton>
      </Stack>
    </Stack>;
};

const StatsTab = ()=>{
    let filter = useStore($statsFilter);
    return <Stack direction="column" gap={2}>
      <StatsFilterComp/>
      <Divider/>
      {filter.keys.map(v=><StatKeyContainer key={v} value={v}/>)}
      {!!filter.keys.length&&<StatsGraph/>}
    </Stack>;
};

const TABS = {
    JOBS: {
        label: `ג'ובים`,
        Comp: JobsTab,
    },
    ERRORS: {
        label: 'שגיאות',
        Comp: ErrorsTab,
    },
    STATS: {
        label: 'סטטיסטיקות',
        Comp: StatsTab,
    },
};

const TabsHeader = props=>{
    let {value, onChange} = props;
    let items = useMemo(()=>_.entries(TABS).map(([k, v])=>({value: k, label: v.label})), []);
    return <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" flexWrap="wrap-reverse">
      <Box flexGrow={1} sx={{borderBottom: 1, borderColor: 'divider'}}>
        <Tabs value={value} onChange={(__, v)=>onChange(v)}>
          {items.map(item=><Tab key={item.value} {...item}/>)}]
        </Tabs>
      </Box>
    </Stack>;
};

const AdminMetrics = ()=>{
    let [tab, setTab] = useState(_.keys(TABS)[0]);
    let Comp = TABS[tab].Comp;
    return <Container maxWidth="lg" sx={{marginTop: 4, marginBottom: 12}}>
      <Stack direction="column" gap={2}>
        <Typography textAlign="center" variant="h2">מטריקות</Typography>
        <TabsHeader value={tab} onChange={setTab}/>
        <Comp/>
      </Stack>
    </Container>;
};

export default withMainContainer(AdminMetrics);
